import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChatBubbleLeftRightIcon, 
  UserIcon, 
  PhoneIcon, 
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import CallButton from '../Call/CallButton';
import StartMeetingButton from '../Meeting/StartMeetingButton';

const AgentPreview = ({ agent, onChatClick, binId }) => {
  const navigate = useNavigate();
  if (!agent) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center">
          <UserIcon className="h-6 w-6 text-yellow-600 mr-3" />
          <div>
            <h3 className="text-lg font-semibold text-yellow-900">
              No Agent Selected
            </h3>
            <p className="text-yellow-800">
              Please select an agent to validate your materials before selling.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const displayName =
    [agent.firstName, agent.lastName].filter(Boolean).join(" ").trim() ||
    agent.username ||
    agent.agentDetails?.agent_name ||
    "Agent";

  const getWorkingHours = () => {
    if (!agent.agentDetails?.openHours) return 'Not specified';

    const today = new Date()
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();
    const dayHours = agent.agentDetails.openHours[today];
    
    if (!dayHours || !dayHours.open || !dayHours.close) {
      return 'Not specified';
    }
    
    return `${dayHours.open} - ${dayHours.close}`;
  };

  const getCurrentStatus = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
    
    const today = new Date()
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();
    const dayHours = agent.agentDetails?.openHours?.[today];
    
    if (!dayHours || !dayHours.open || !dayHours.close) {
      return { status: 'unknown', text: 'Status unknown', color: 'text-gray-500' };
    }
    
    const openTime = dayHours.open;
    const closeTime = dayHours.close;
    
    if (currentTime >= openTime && currentTime <= closeTime) {
      return { status: 'online', text: 'Online', color: 'text-green-600' };
    } else {
      return { status: 'offline', text: 'Offline', color: 'text-red-600' };
    }
  };

  const status = getCurrentStatus();

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mr-4">
              {agent.agentDetails?.agentImage ? (
                <img 
                  src={agent.agentDetails.agentImage} 
                  alt={displayName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <UserIcon className="h-6 w-6 text-green-600" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {displayName}
              </h3>
              <p className="text-green-100 text-sm">
                Your Assigned Agent
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <CallButton
              calleeId={agent._id}
              binId={binId}
              calleeName={displayName}
              variant="icon"
            />
            <StartMeetingButton
              participantId={agent._id}
              participantName={displayName}
            />
            <button
              onClick={() => onChatClick(binId)}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all duration-200 hover:scale-105"
              title="Chat with agent"
            >
              <ChatBubbleLeftRightIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Agent Details */}
      <div className="p-6 space-y-4">
        {/* Spotlighted Locations */}
        {agent.spotlightLocations && agent.spotlightLocations.length > 0 && (
          <div className="pb-4 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
              <MapPinIcon className="h-4 w-4 mr-2 text-green-600" />
              Spotlighted Pickup Locations
            </h4>
            <ul className="space-y-2">
              {agent.spotlightLocations.map((item, index) => (
                <li key={index} className="flex items-center text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
                  <MapPinIcon className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                  <span className="font-medium">{item.location || '—'}</span>
                  {item.time && <span className="text-gray-600 ml-2">• {item.time}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${
              status.status === 'online' ? 'bg-green-500' : 
              status.status === 'offline' ? 'bg-red-500' : 'bg-gray-400'
            }`}></div>
            <span className={`text-sm font-medium ${status.color}`}>
              {status.text}
            </span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Verified Agent
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-3">
          <div className="flex items-center">
            <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Phone</p>
              <p className="font-medium text-gray-900">
                {agent.phoneNumber || agent.agentDetails?.phone_number || 'Not provided'}
              </p>
            </div>
          </div>

          <div className="flex items-center">
            <MapPinIcon className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Location</p>
              <p className="font-medium text-gray-900">
                {agent.state || agent.homeAddress || "—"}
              </p>
              {agent.agentDetails?.localGovernmentAddress && (
                <p className="text-sm text-gray-600">
                  {agent.agentDetails.localGovernmentAddress}
                </p>
              )}
              {agent.agentDetails?.streetAddress && (
                <p className="text-sm text-gray-600">
                  {agent.agentDetails.streetAddress}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Working Hours (Today)</p>
              <p className="font-medium text-gray-900">
                {getWorkingHours()}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex gap-3">
            <CallButton
              calleeId={agent._id}
              binId={binId}
              calleeName={displayName}
              className="flex-1"
              variant="primary"
            />
            <StartMeetingButton
              participantId={agent._id}
              participantName={displayName}
              className="flex-1"
            />
            <button
              onClick={() => onChatClick(binId)}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
              Chat
            </button>
            <button
              onClick={() => navigate("/agents")}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              Change Agent
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentPreview;
